import React, { useState, useMemo, useEffect } from 'react';
import { SmartDataTable } from '@src/components/SmartDataTable';
import {
  useAppointmentsColumns,
  useAppointmentsFormFields,
  openPatientProfileInNewTab,
} from '../hooks';
import { usePatientFormFields, CreateForm, EditForm } from '@src/components/shared/forms';
import { Button, Tag, Spin, Tooltip, message } from 'antd';
import { UserOutlined, PlusOutlined, DollarOutlined, EditOutlined } from '@ant-design/icons';
import { useInvalidate, useOne } from '@refinedev/core';
import { usePatientDues, useClinicDues } from '@src/hooks';
import { cleanPhoneNumber } from '@src/utils/phoneUtils';

interface AppointmentsListProps {
  onViewChange: (view: 'list' | 'calendar') => void;
  viewTabs: React.ReactNode;
  patientId?: string | null;
  searchQuery?: string | null;
  onCreateDrawerClose?: () => void;
  autoEditAppointmentId?: string | null;
  onAutoEditComplete?: () => void;
}

export const AppointmentsList: React.FC<AppointmentsListProps> = ({
  onViewChange,
  viewTabs,
  patientId,
  searchQuery,
  onCreateDrawerClose,
  autoEditAppointmentId,
  onAutoEditComplete,
}) => {
  const invalidate = useInvalidate();
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [createPatientFormVisible, setCreatePatientFormVisible] =
    useState(false);
  const [editPatientFormVisible, setEditPatientFormVisible] = useState(false);
  const [editPatientId, setEditPatientId] = useState<string | number | null>(null);
  const [formKey, setFormKey] = useState(0);

  const columns = useAppointmentsColumns({ showCreatedAt: true }); // You can set this to false to hide the column
  const formFields = useAppointmentsFormFields(patientId || undefined, selectedRecord);
  const patientFormFields = usePatientFormFields();

  // Fetch appointment data when auto-edit is triggered
  const { data: autoEditAppointmentData } = useOne({
    resource: 'appointments',
    id: autoEditAppointmentId || '',
    queryOptions: {
      enabled: !!autoEditAppointmentId,
    },
  });

  // Fetch patient data for editing
  const { data: patientData, isLoading: patientDataLoading } = useOne({
    resource: 'patients',
    id: editPatientId || '',
    queryOptions: {
      enabled: !!editPatientId && editPatientFormVisible,
    },
  });

  // Set selectedRecord when auto-edit appointment data is fetched
  useEffect(() => {
    if (autoEditAppointmentData?.data && autoEditAppointmentId) {
      setSelectedRecord(autoEditAppointmentData.data);
    }
  }, [autoEditAppointmentData, autoEditAppointmentId]);

  // Effect to handle auto-edit completion and fetch appointment data
  useEffect(() => {
    if (autoEditAppointmentId && onAutoEditComplete) {
      // Clear the auto edit ID after a short delay to allow the form to open
      const timer = setTimeout(() => {
        onAutoEditComplete();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [autoEditAppointmentId, onAutoEditComplete]);

  // Get patient dues for the selected record
  const { dues: patientDues, loading: patientDuesLoading } = usePatientDues(
    selectedRecord?.patient_id || null
  );

  // Get clinic dues for the selected record
  const { dues: clinicDues, loading: clinicDuesLoading } = useClinicDues(
    selectedRecord?.patient_id || null
  );

  const handlePatientRedirect = (id: string | number) => {
    openPatientProfileInNewTab(id);
  };

  // Function to handle create patient
  const handleCreatePatient = () => {
    setCreatePatientFormVisible(true);
  };

  // Function to handle edit patient
  const handleEditPatient = (patientId: string | number) => {
    setEditPatientId(patientId);
    setEditPatientFormVisible(true);
  };

  // Get patient data from selected record for pre-filling the patient form
  const getPatientInitialValues = () => {
    if (!selectedRecord) return {};

    // Extract patient data from the appointment record
    const patientName = selectedRecord.patient_name || '';

    // Handle mobile number with proper null checks using cleanPhoneNumber utility
    const patientMobile = cleanPhoneNumber(
      selectedRecord.patient_mobile_no || ''
    );

    // Split patient name into first and last name
    const nameParts = patientName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    return {
      first_name: firstName,
      last_name: lastName,
      mobile_no: patientMobile,
      appointment_id: selectedRecord.id, // Include appointment_id in the form
    };
  };

  // Function to handle patient creation success
  const handlePatientCreated = (data: any) => {
    setCreatePatientFormVisible(false);

    // Update the selected record with the new patient data
    if (selectedRecord && data?.data?.data) {
      const newPatient = data.data.data; // Access the actual patient data

      const updatedRecord = {
        ...selectedRecord,
        patient_id: newPatient.id,
        patient_name:
          `${newPatient.first_name || ''} ${newPatient.last_name || ''}`.trim(),
        patient_mobile_no: newPatient.mobile_no,
      };

      setSelectedRecord(updatedRecord);

      // Force form refresh by updating the key
      // This will make the SmartDataTable re-render with updated data
      setFormKey(prev => prev + 1);

      // Invalidate appointments cache to refresh the appointment form
      // This will make the appointment form reflect the new patient_id
      invalidate({
        resource: 'appointments',
        invalidates: ['list', 'many'],
      });

      // Also invalidate patients cache to ensure fresh data
      invalidate({
        resource: 'patients',
        invalidates: ['list', 'many'],
      });
    }
  };

  // Function to handle patient edit success
  const handlePatientEdited = (data: any) => {
    setEditPatientFormVisible(false);
    setEditPatientId(null);

    // Update the selected record with the edited patient data
    if (selectedRecord && data?.data?.data) {
      const editedPatient = data.data.data;

      const updatedRecord = {
        ...selectedRecord,
        patient_name:
          `${editedPatient.first_name || ''} ${editedPatient.last_name || ''}`.trim(),
        patient_mobile_no: editedPatient.mobile_no,
      };

      setSelectedRecord(updatedRecord);

      // Force form refresh by updating the key
      setFormKey(prev => prev + 1);

      // Invalidate appointments cache to refresh the appointment form
      invalidate({
        resource: 'appointments',
        invalidates: ['list', 'many'],
      });

      // Also invalidate patients cache to ensure fresh data
      invalidate({
        resource: 'patients',
        invalidates: ['list', 'many'],
      });
    }
  };

  // Create custom title component with View Patient button and Due Amount tag
  const createCustomTitle = (patientId: string | number | null) => {
    const patientDueAmount = patientDues?.total_due || 0;
    const clinicDueAmount = clinicDues?.total_due || 0;
    const hasPatientDues = patientDueAmount > 0;
    const hasClinicDues = clinicDueAmount > 0;
    const isLoading = patientDuesLoading || clinicDuesLoading;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>Edit Appointment</span>
          {patientId !== 0 && (hasPatientDues || hasClinicDues) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isLoading ? (
                <Spin size="small" />
              ) : (
                <>
                  {/* Patient Dues Tag */}
                  {hasPatientDues && (
                    <Tooltip
                      title={`Patient Due: ₹${patientDueAmount.toLocaleString()}`}
                      placement="top"
                    >
                      <Tag
                        color="orange"
                        icon={<DollarOutlined />}
                        style={{ margin: 0, cursor: 'pointer' }}
                      >
                        Patient: ₹ {patientDueAmount.toLocaleString()}
                      </Tag>
                    </Tooltip>
                  )}

                  {/* Clinic Dues Tag */}
                  {hasClinicDues && (
                    <Tooltip
                      title={`Clinic Due: ₹${clinicDueAmount.toLocaleString()}`}
                      placement="top"
                    >
                      <Tag
                        color="red"
                        icon={<DollarOutlined />}
                        style={{ margin: 0, cursor: 'pointer' }}
                      >
                        Clinic: ₹ {clinicDueAmount.toLocaleString()}
                      </Tag>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {patientId ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              type="default"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditPatient(patientId)}
            >
              Edit Patient
            </Button>
            <Button
              type="primary"
              icon={<UserOutlined />}
              size="small"
              onClick={() => handlePatientRedirect(patientId)}
            >
              View Patient
            </Button>
          </div>
        ) : (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            onClick={handleCreatePatient}
            style={{ marginLeft: '16px' }}
          >
            Create Patient
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-hidden">
      <SmartDataTable
        key={formKey}
        api={{
          resource: 'appointments',
        }}
        title=""
        columns={columns}
        search={{
          enabled: true,
          fields: ['patient_name', 'patient_mobile_no', 'id'],
          placeholder: 'Search by patient name, mobile, or ID...',
          debounceMs: 500,
          initialValue: searchQuery || '',
        }}
        createForm={{
          fields: formFields as any,
          title: 'Create Appointment',
          submitText: 'Create Appointment',
          initialValues: patientId ? { patient_id: patientId } : undefined,
        }}
        editForm={{
          fields: formFields as any,
          title: 'Edit Appointment',
          submitText: 'Save Changes',
          customTitle: selectedRecord
            ? createCustomTitle(selectedRecord.patient_id)
            : undefined,
        }}
        viewTabs={viewTabs}
        onViewChange={onViewChange}
        formDisplayMode="drawer"
        drawerWidth={1200}
        showCreate={true}
        showEdit={true}
        showDelete={true}
        showView={false}
        pageSize={10}
        showSizeChanger={true}
        showQuickJumper={false}
        // showExport={true}
        showColumnVisibility={true}
        tableProps={{
          scroll: {
            x: 'max-content',
            y: 'calc(100vh - 240px)',
          },
        }}
        rowKey="id"
        // Chat configuration
        enableChat={true}
        chatType="APPOINTMENT"
        chatPatientId={
          selectedRecord?.patient_id ||
          (patientId ? parseInt(patientId) : undefined)
        }
        chatTitle={
          selectedRecord?.patient_id
            ? 'Patient Messages'
            : 'Appointment Messages'
        }
        chatPlaceholder={
          selectedRecord?.patient_id
            ? 'Add a note for patient...'
            : 'Add a note for appointment...'
        }
        isintialScroll={true}
        // Use patient_id as chatRelativeId - prioritize selectedRecord patient_id, fallback to URL patientId
        chatRelativeId={
          selectedRecord?.patient_id ||
          (patientId ? parseInt(patientId) : undefined)
        }
        // Auto-open create drawer when patientId is provided
        autoOpenCreate={!!patientId}
        // Handle create drawer close
        onCreateDrawerClose={onCreateDrawerClose}
        // Handle edit click to capture selected record
        onEditClick={record => {
          // Store the record for use in customTitle
          setSelectedRecord(record);
        }}
        autoOpenEditId={autoEditAppointmentId}
      />

      {/* Patient Creation Form */}
      <CreateForm
        resource="patients"
        fields={patientFormFields}
        title="Create New Patient"
        submitText="Create Patient"
        open={createPatientFormVisible}
        onClose={() => setCreatePatientFormVisible(false)}
        onSuccess={handlePatientCreated}
        formDisplayMode="drawer"
        drawerWidth={800}
        initialValues={getPatientInitialValues()}
      />

      {/* Patient Edit Form */}
      {editPatientId && editPatientFormVisible && (
        <EditForm
          resource="patients"
          fields={patientFormFields}
          title={patientDataLoading ? "Loading Patient Data..." : "Edit Patient"}
          submitText="Update Patient"
          open={editPatientFormVisible}
          onClose={() => {
            setEditPatientFormVisible(false);
            setEditPatientId(null);
          }}
          onSuccess={handlePatientEdited}
          formDisplayMode="drawer"
          drawerWidth={800}
          initialValues={patientData?.data ? { ...patientData.data, id: editPatientId } : { id: editPatientId }}
          rowKey="id"
        />
      )}
    </div>
  );
};
